<script setup lang="ts">
import { computed, watch, ref, reactive, nextTick, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useLanguages } from '@/composables/translations/useLanguages'
import type { PeriodRange } from '@/types/Common'
import { createOAudioController, type CalendarPresetId } from '@ai/ui-components'
import type { DisconnectedBy, VaLanguage } from 'shared'
import ColdMonitoringTable from '@/components/BusinessLogic/Widgets/ColdMonitoringTable.vue'
import ColdMonitoringStats from '@/components/BusinessLogic/Widgets/ColdMonitoringStats.vue'
import { useColdMonitoringCoordinator } from '@/composables/ColdMonitoring/useColdMonitoringCoordinator'
import ColdMonitoringChartModal from '@/components/BusinessLogic/Modal/ColdMonitoringChartModal.vue'
import ConfigAgentSelect from '@/components/ConfigAgentSelect.vue'
import { useColdMonitoringApi } from '@/composables/api/useColdMonitoringApi'
import type { ColdMonitoringResponseCall } from 'shared'
import ColdCallModal from '@/components/BusinessLogic/Modal/ColdCallModal.vue'
import { useToast } from '@ai/ui-components/toast'
import { useQueryClient } from '@tanstack/vue-query'
import fetchApi, { FetchApiError } from '@/utils/fetchApi'
import type { ColdMonitoringCallDetailsResponse } from 'shared'
import { coldCallDetailsQueryKey } from '@/composables/queries/cold-monitoring/useColdCallDetailsQuery'
import ErrorPage from '@/views/ErrorPage.vue'
import ResetFiltersButton from '@/components/ResetFiltersButton.vue'
import { useJsonDownload } from '@/composables/ColdMonitoring/useJsonDownload'
import { useRecordingProviderCache } from '@/composables/ColdMonitoring/useRecordingProviderCache'
import ReportInteractionPanel from '@/components/BusinessLogic/CallDetails/ReportInteractionPanel.vue'
import { isNotBlank } from '@/utils/String'

const SEARCHABLE_GTE = 6

const { t, locale } = useI18n()
const { languageLabel } = useLanguages()
const toast = useToast()

const {
  callsList,
  filters,
  availableFilters,
  pagination,
  status,
  applyFilters,
  calendarRange,
  calendarPresetId,
  setCalendarRange,
  setCalendarPresetId,
  applyCalendarFilter,
  clearCalendarFilter,
  setPage,
  setPageSize,
  // Stats
  stats: statsKpis,
  isLoadingStats: statsLoading,
  statsError,
  // Charts
  isModalOpen: isChartModalOpen,
  chartProps,
  chartInterval,
  chartPeriod,
  chartView,
  setChartView,
  showChartToggle,
  showChartIntervalDropdown,
  intervalOptions,
  modalTitle,
  openModal: openChartModal,
  closeModal: closeChartModal,
  resetFilters,
} = useColdMonitoringCoordinator()

watch(isChartModalOpen, (value) => {
  if (!value) closeChartModal()
})

const selectedAgents = computed({
  get: () => filters.value.agents,
  set: (value: string[]) => {
    void applyFilters({ agents: value })
  },
})

const selectedLanguages = computed<VaLanguage[]>({
  get: () => filters.value.languages,
  set: (value: VaLanguage[]) => {
    void applyFilters({ languages: value })
  },
})

const selectedDisconnectedBy = computed<DisconnectedBy[]>({
  get: () => filters.value.disconnectedBy,
  set: (value: DisconnectedBy[]) => {
    void applyFilters({ disconnectedBy: value })
  },
})

const showLiveTests = computed<boolean>({
  get: () => filters.value.showLiveTests,
  set: (value: boolean) => {
    void applyFilters({ showLiveTests: value })
  },
})

const dateRange = computed<PeriodRange>({
  get: () => calendarRange.value,
  set: (range) => {
    setCalendarRange(range)
  },
})

const dateRangePresetId = computed<CalendarPresetId | null>({
  get: () => calendarPresetId.value,
  set: (presetId) => {
    setCalendarPresetId(presetId)
  },
})

const agentOptions = computed(() =>
  availableFilters.value.agents.reduce<Record<string, string>>((acc, a) => {
    acc[a.id] = a.name || t('deleted_agent')
    return acc
  }, {}),
)

const languageOptions = computed(() =>
  availableFilters.value.languages.map((lang) => ({
    value: lang,
    label: languageLabel(locale.value, lang),
  })),
)

const disconnectedByOptions = computed(() =>
  availableFilters.value.disconnectedBy.map((item) => ({
    value: item,
    label: t(item),
  })),
)

const isLoading = computed(() => status.value.isLoading)
const error = computed(() => status.value.error)

const hasActivePeriod = (period: PeriodRange | null) =>
  isNotBlank(period?.startDate) || isNotBlank(period?.endDate)
const hasActiveFilters = computed(
  () =>
    hasActivePeriod(filters.value.period) ||
    filters.value.agents.length > 0 ||
    filters.value.languages.length > 0 ||
    filters.value.disconnectedBy.length > 0 ||
    filters.value.showLiveTests,
)

/***************************
 * Table Related logics
 */
// audio/details hydration
const audioController = createOAudioController({ defaultPlaybackRate: 1.2 })
interface HydratedRecording {
  sourceUrl?: string
  recordingUrl?: string
  expiresAt?: string
  ttlSeconds?: number
}

const hydratedAudio = reactive<Record<string, HydratedRecording | undefined>>({})
let audioRequestId = 0
const { getOrCreateRecordingProvider, disposeRecordingProviders } = useRecordingProviderCache()
const { fetchColdCallDetails } = useColdMonitoringApi({
  period: ref(undefined),
  agents: ref(undefined),
})

const toHydratedRecording = (
  audio: ColdMonitoringCallDetailsResponse['audio'] | null | undefined,
): HydratedRecording => ({
  sourceUrl: audio?.url,
  recordingUrl: audio?.recordingUrl,
  expiresAt: audio?.expiresAt,
  ttlSeconds: audio?.ttlSeconds,
})

const getPreferredRecordingUrl = (recording: HydratedRecording | undefined) =>
  // TODO(AI-9672): recordingUrl is a same-origin BFF proxy for Rados signed URLs because
  // browser-side waveform reads are blocked by S3/Rados CORS on SBX. If this proxy shows
  // production issues under real traffic (streaming cost, latency, Range handling, upstream
  // failures), evaluate changing CORS rules on the S3/Rados service that serves the signed
  // https://rados... URLs instead of routing audio bytes through BFF.
  recording?.recordingUrl ?? recording?.sourceUrl ?? ''

const enrichedCalls = computed<ColdMonitoringResponseCall[]>(() =>
  callsList.value.map((call) => {
    const hydratedRecording = hydratedAudio[call.callInfo.id]

    return {
      ...call,
      callInfo: {
        ...call.callInfo,
        // undefined -> unknown/not fetched, '' -> unavailable, string -> ready
        audioUrl:
          hydratedRecording !== undefined
            ? getPreferredRecordingUrl(hydratedRecording)
            : typeof call.callInfo.audioUrl === 'string'
              ? call.callInfo.audioUrl
              : undefined,
      },
    }
  }),
)

const callRows = computed(() =>
  enrichedCalls.value.map((call) => {
    const info = call.callInfo
    const durationSec = Math.max(0, Math.floor((info.endTime - info.startTime) / 1000))
    return { ...info, durationSec }
  }),
)

const { downloadCallJson } = useJsonDownload({
  getCallById: (callId: string) => callRows.value.find((item) => item.id === callId),
  fetchCallDetails: (callId: string) => fetchColdCallDetails(callId, { suppressAlert: true }),
})

const queryClient = useQueryClient()
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-redundant-type-constituents
const callModalRef = ref<InstanceType<typeof ColdCallModal> | null>(null)

const reportingCallId = ref<string | null>(null)
const reportPanelOpen = ref(false)

const openReportPanel = (callId: string) => {
  reportingCallId.value = callId
  reportPanelOpen.value = true
}

watch(reportPanelOpen, (open) => {
  if (!open) reportingCallId.value = null
})

onBeforeUnmount(() => {
  audioController.dispose()
  disposeRecordingProviders()
})

const route = useRoute()
const router = useRouter()

const routeCallId = computed(() =>
  typeof route.query.callId === 'string' ? route.query.callId : null,
)
let detailsRequestId = 0

const clearCallIdFromUrl = () => {
  void router.replace({
    path: route.path,
    query: {
      ...route.query,
      callId: undefined,
    },
  })
}

const setCallIdInUrl = (callId: string) => {
  void router.replace({
    path: route.path,
    query: {
      ...route.query,
      callId,
    },
  })
}

const callDetailsTo = (callId: string) => ({
  path: route.path,
  query: {
    ...route.query,
    callId,
  },
})

const coldCallDetailsFetcher = (callId: string, date?: string) => {
  const endpoint = date
    ? `/api/cold-monitoring/${callId}/details?date=${date}`
    : `/api/cold-monitoring/${callId}/details`
  return fetchApi<ColdMonitoringCallDetailsResponse>(
    { endpoint, method: 'GET' },
    { suppressAlert: true },
  )
}

const validateAndOpenPanel = async (callId: string, requestId: number) => {
  try {
    await queryClient.fetchQuery({
      queryKey: coldCallDetailsQueryKey(callId),
      queryFn: () => coldCallDetailsFetcher(callId),
      staleTime: 5 * 60 * 1000,
      retry: false,
    })
    if (requestId !== detailsRequestId || routeCallId.value !== callId) return
    await nextTick()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    callModalRef.value?.openPanel(callId)
  } catch (err) {
    if (requestId !== detailsRequestId || routeCallId.value !== callId) return
    if (err instanceof FetchApiError && [403, 404, 422].includes(err.status)) {
      toast.error(t('cold_monitoring.call_not_found'), { title: t('error_occurred') })
    }
    clearCallIdFromUrl()
  }
}

// The existing ?callId contract is the source of truth for direct load and link activation.
// nextTick ensures ColdCallModal has mounted (v-if="callRows.length") before openPanel is called
watch(
  [callRows, routeCallId],
  async ([rows, callId]) => {
    if (rows.length === 0 || !callId) return
    await validateAndOpenPanel(callId, ++detailsRequestId)
  },
  { immediate: true },
)

// Watch panel close to clear ?callId from URL
watch(
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
  () => callModalRef.value?.panelOpen ?? false,
  (isOpen, wasOpen) => {
    if (wasOpen && !isOpen) {
      clearCallIdFromUrl()
    }
  },
)

const handleRequestAudio = async (callId: string) => {
  const currentAudioState = audioController.state.value
  if (currentAudioState.activeTrack?.id === callId && currentAudioState.status === 'playing') {
    audioController.stop()
    return
  }
  if (currentAudioState.activeTrack?.id === callId && currentAudioState.status === 'paused') {
    try {
      await audioController.resume()
    } catch {
      toast.error(t('cold_monitoring.audio_error'), { title: t('error_occurred') })
    }
    return
  }
  if (currentAudioState.activeTrack?.id === callId && currentAudioState.status === 'loading') {
    return
  }

  const requestId = ++audioRequestId
  try {
    let recording = hydratedAudio[callId]
    if (recording === undefined) {
      const details = await fetchColdCallDetails(callId, { suppressAlert: true })
      if (requestId !== audioRequestId) return

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (details) {
        queryClient.setQueryData(coldCallDetailsQueryKey(callId), details)
      }
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      recording = toHydratedRecording(details?.audio)
      hydratedAudio[callId] = recording
    }
    if (requestId !== audioRequestId) return

    const src = getPreferredRecordingUrl(recording)
    if (!src) {
      toast.error(t('cold_monitoring.audio_error'), { title: t('error_occurred') })
      return
    }
    await audioController.play({ id: callId, provider: getOrCreateRecordingProvider(callId, src) })
  } catch {
    if (requestId !== audioRequestId) return
    toast.error(t('cold_monitoring.audio_error'), { title: t('error_occurred') })
  }
}
</script>

<template>
  <div class="flex flex-col h-full overflow-hidden">
    <template v-if="error">
      <ErrorPage class="h-full" :description="error" :show-actions="false" />
    </template>
    <div v-else class="flex flex-col space-y-6 select-none sticky top-0 z-20 pt-10 px-10 pb-4">
      <h1 class="typography-heading-1">{{ t('navigation.cold_monitoring') }}</h1>

      <div class="flex flex-wrap justify-between items-center gap-y-3">
        <div
          data-testid="cold-monitoring-filters"
          role="search"
          :aria-label="t('navigation.cold_monitoring')"
          class="flex space-x-m"
        >
          <OCalendar
            v-model="dateRange"
            v-model:presetId="dateRangePresetId"
            size="L"
            simple-range
            @apply="applyCalendarFilter"
            @clear="clearCalendarFilter"
          />

          <ConfigAgentSelect
            v-model="selectedAgents"
            :options="agentOptions"
            :searchable="Object.entries(agentOptions).length >= SEARCHABLE_GTE"
            :title="t('agent')"
          />

          <ODropdown
            v-model="selectedLanguages"
            :options="languageOptions"
            medium
            multi-select
            :searchable="languageOptions.length >= SEARCHABLE_GTE"
            :filter-title="t('language')"
            filter
          />

          <ODropdown
            v-model="selectedDisconnectedBy"
            :options="disconnectedByOptions"
            medium
            multi-select
            :searchable="disconnectedByOptions.length >= SEARCHABLE_GTE"
            :filter-title="t('disconnected_by')"
            filter
          />

          <ResetFiltersButton :visible="hasActiveFilters" @reset="resetFilters" />
        </div>

        <OToolTip :content="t('cold_monitoring.live_test_toggle.tooltip')">
          <span class="inline-flex">
            <OSwitch v-model="showLiveTests" :label="t('cold_monitoring.live_test_toggle.label')" />
          </span>
        </OToolTip>
      </div>
    </div>

    <div
      v-if="!error"
      class="flex-1 flex flex-col min-h-0 space-y-6 px-10 pb-10 overflow-y-auto pt-2"
    >
      <div>
        <ColdMonitoringStats
          :kpis="statsKpis"
          :loading="statsLoading"
          :error="statsError ?? undefined"
          @expand="openChartModal"
        />
      </div>

      <div class="flex-1 min-h-[450px]">
        <OCardAdvanced class="w-full h-full" elevation="S">
          <div class="col-span-2 flex-0 min-h-0">
            <ColdMonitoringTable
              :calls="enrichedCalls"
              :pagination="pagination"
              :loading="isLoading"
              :audio-controller="audioController"
              :details-to="callDetailsTo"
              @update:currentPage="setPage"
              @update:itemsPerPage="setPageSize"
              @request-audio="handleRequestAudio"
              @download-call="downloadCallJson"
              @report-call="openReportPanel"
            />
          </div>
        </OCardAdvanced>
      </div>
    </div>
  </div>

  <ColdMonitoringChartModal
    :title="modalTitle"
    :chart-props="chartProps"
    :interval-options="intervalOptions"
    :chart-view="chartView"
    :set-chart-view="setChartView"
    :show-chart-toggle="showChartToggle"
    :show-chart-interval-dropdown="showChartIntervalDropdown"
    v-model:chartPeriod="chartPeriod"
    v-model:open="isChartModalOpen"
    v-model:chartInterval="chartInterval"
  />

  <ColdCallModal
    v-if="callRows.length"
    ref="callModalRef"
    :item="callRows[0]"
    :calls="callRows"
    :audio-controller="audioController"
    :get-recording-provider="getOrCreateRecordingProvider"
    @download-call="downloadCallJson"
    @navigate-call="setCallIdInUrl"
  />

  <ReportInteractionPanel
    v-if="reportingCallId"
    :call-id="reportingCallId"
    v-model:open="reportPanelOpen"
  />
</template>
